import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Button,
  DeleteConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
  Textarea,
  cn,
  type AssigneeLookup
} from '@bliks-io/ui';
import type { StepTemplate } from '@bliks-io/contracts';
import { useStepTemplates } from '@/api/queries/useStepTemplates';
import { useDeleteStepTemplate, useUpdateStepTemplate } from '@/api/mutations/useStepTemplates';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { formatDate } from '@/lib/formatters';
import { StepTemplateThumbnail } from './StepTemplateThumbnail';

interface Props {
  onInsert: (template: StepTemplate) => void;
  onClose: () => void;
  isInserting: boolean;
  assigneeLookup?: AssigneeLookup;
}

function EditFields({
  template,
  onSave,
  onCancel,
  isSaving
}: {
  template: StepTemplate;
  onSave: (patch: { name: string; description: string }) => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description);
  return (
    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
      <Input
        aria-label={t('stepTemplates.saveDialog.name')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoFocus
      />
      <Textarea
        aria-label={t('stepTemplates.saveDialog.description')}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
      />
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="small" onClick={onCancel}>
          {t('stepTemplates.insertDialog.cancel')}
        </Button>
        <Button
          size="small"
          loading={isSaving}
          disabled={!name.trim()}
          onClick={() => onSave({ name: name.trim(), description: description.trim() })}
        >
          {t('stepTemplates.insertDialog.save')}
        </Button>
      </div>
    </div>
  );
}

export function InsertStepTemplateDialog({
  onInsert,
  onClose,
  isInserting,
  assigneeLookup
}: Props) {
  const { t, i18n } = useTranslation();
  const { data: templates = [], isPending, isError } = useStepTemplates();
  const updateTemplate = useUpdateStepTemplate();
  const deleteTemplate = useDeleteStepTemplate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<StepTemplate | null>(null);

  const selected = templates.find((template) => template._id === selectedId) ?? null;

  const handleUpdate = (id: string, body: { name: string; description: string }) => {
    updateTemplate.mutate(
      { id, body },
      {
        onSuccess: () => setEditingId(null),
        onError: (error) => toastApiError(error, 'stepTemplates.error')
      }
    );
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    const id = pendingDelete._id;
    deleteTemplate.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null);
        setSelectedId((current) => (current === id ? null : current));
      },
      onError: (error) => toastApiError(error, 'stepTemplates.error')
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !isInserting && onClose()}>
      <DialogContent className="max-w-3xl" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{t('stepTemplates.insertDialog.title')}</DialogTitle>
        </DialogHeader>
        {/*
         * A `listbox` may only contain `option`/`group` children, so the role
         * is set only once there are rows to hold.
         * ponytail: the per-row edit/delete icon buttons are inside their
         * `option` and so are not separately operable by a screen reader
         * (same shape as `EntityPickerBucketsList.tsx:54-63`); upgrade to a
         * `grid` with `row`/`gridcell`, or a per-row menu, if that bites.
         */}
        <div
          role={templates.length > 0 ? 'listbox' : undefined}
          aria-label={templates.length > 0 ? t('stepTemplates.insertDialog.title') : undefined}
          className="-mx-6 max-h-[60vh] divide-y overflow-y-auto border-y"
        >
          {isPending && (
            <div role="status" aria-label={t('common.loading')} className="divide-y">
              {[0, 1, 2].map((row) => (
                <div key={row} className="flex gap-4 px-6 py-3">
                  <Skeleton className="h-28 w-52 shrink-0" />
                  <div className="flex flex-1 flex-col gap-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {isError && (
            <div className="px-6 py-10 text-center text-sm text-destructive">
              {t('stepTemplates.insertDialog.loadError')}
            </div>
          )}
          {!isPending && !isError && templates.length === 0 && (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              {t('stepTemplates.insertDialog.empty')}
            </div>
          )}
          {templates.map((template) => (
            <div
              key={template._id}
              role="option"
              aria-selected={selectedId === template._id}
              tabIndex={0}
              onClick={() => setSelectedId(template._id)}
              onDoubleClick={() => onInsert(template)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                setSelectedId(template._id);
              }}
              className={cn(
                'flex cursor-pointer gap-4 px-6 py-3 transition-colors hover:bg-accent/50',
                selectedId === template._id && 'bg-accent'
              )}
            >
              <StepTemplateThumbnail
                template={template}
                assigneeLookup={assigneeLookup}
                className="w-52 shrink-0"
              />
              <div className="flex min-w-0 flex-1 flex-col">
                {editingId === template._id ? (
                  <EditFields
                    template={template}
                    isSaving={updateTemplate.isPending}
                    onSave={(patch) => handleUpdate(template._id, patch)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium">{template.name}</div>
                      <div className="flex shrink-0 gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('stepTemplates.insertDialog.edit')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingId(template._id);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive"
                          aria-label={t('stepTemplates.insertDialog.delete')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete(template);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="line-clamp-2 text-sm text-muted-foreground">
                      {template.description}
                    </div>
                    <div className="mt-auto pt-2 text-xs text-muted-foreground">
                      {t('stepTemplates.insertDialog.meta', {
                        count: template.steps.length + template.events.length,
                        author: template.createdBy.name,
                        date: formatDate(template.createdAt, i18n.language)
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isInserting}>
            {t('stepTemplates.insertDialog.cancel')}
          </Button>
          <Button
            loading={isInserting}
            disabled={!selected}
            onClick={() => selected && onInsert(selected)}
          >
            {t('stepTemplates.insertDialog.insert')}
          </Button>
        </DialogFooter>
      </DialogContent>
      <DeleteConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        onConfirm={handleDelete}
        isDeleting={deleteTemplate.isPending}
        title={t('stepTemplates.insertDialog.deleteConfirm.title')}
        description={t('stepTemplates.insertDialog.deleteConfirm.body', {
          name: pendingDelete?.name ?? ''
        })}
      />
    </Dialog>
  );
}
